import { createSlice } from '@reduxjs/toolkit';
import { fetchGet } from '../../helpers/fetching';

const initialState = {
  isReady: false,
  client: '',
  client_id: '',
  compression_high: 0,
  compression_low: 0,
  compression_tolerance: 0,
  sensitivity_calcifications: 'medium',
  sensitivity_opacities: 'medium',
  quailty_points_export: false,
  dicom_server_aet: '',
  premium_features: {
    data_annotation: false,
    data_export: false,
    density_module: false,
    diagnostics_microcalc_module: false,
    diagnostics_opacities_module: false,
    model_calibration: false,
    retrospective_processing: false,
    risk_module: false,
    query_worklist: false,
  },
  quality_system: {
    quality_system: 'swiss_derived',
    quality_values: ['perfect', 'good', 'moderate', 'inadequate'],
    overall_quality: false,
    histogram_chart: false,
    quality_features: {
      artifacts_classes: [],
      blur_classes: [],
      breast_volume_classes: [],
      compression_classes: [],
      contrast_classes: [],
      elevation_classes: [],
      exposure_classes: [],
      image_labeling_classes: [],
      imf_classes: [],
      nipple_orientation_classes: [],
      nipple_profile_classes: [],
      noise_classes: [],
      parenchyma_classes: [],
      pect_angle_classes: [],
      pect_level_classes: [],
      pect_shape_classes: [],
      pectoral_muscle_classes: [],
      pnl_diff_classes: [],
      skin_folds_classes: [],
      symmetry_classes: [],
    },
    quality_thresholds: {
      pnl_from_edge: false,
      nipple_angle_lateral_thresholds: [0, 0],
      nipple_angle_medial_thresholds: [0, 0],
      parenchyma_cuts_thresholds: [0, 0],
      pnl_diff_thresholds: [0, 0],
      symmetry_thresholds: [0, 0],
    },
  },
  user_settings: {
    presentation_lesions: 'boxes',
    presentation_quality: 'boxes',
    priors_report: false,
    inverted_view_position: false,
    crop_to_breast: false,
    priors_number_limit: 0,
    priors_years_limit: 0,
  },
  report_configuration: {
    language: 'en',
    file_format: 'jpeg',
    sending_delay: 60,
    contour_shown: true,
    projections_big: false,
    hide_operator_name: false,
    image_compression: false,
    show_missed_findings: false,
    crop_to_breast: false,
    lesions_to_show: {
      calc_vessel: false,
      calc_birads2: false,
      calc_birads3: true,
      calc_birads4: true,
      calc_birads5: true,
      opac_birads2: false,
      opac_birads3: true,
      opac_birads4: true,
      opac_birads5: true,
      opac_lesionKnown: false,
    },
  },
  language: 'en',
};

export const globalSettingsSlice = createSlice({
  name: 'global_settings',
  initialState,
  reducers: {
    setGlobalSettings: (state, action) => {
      const { user_settings, report_configuration, ...rest } = action.payload;
      state.isReady = true;

      state.client = rest.client || '';
      state.client_id = rest.client_id || '';
      state.compression_high = rest.compression_high || 0;
      state.compression_low = rest.compression_low || 0;
      state.compression_tolerance = rest.compression_tolerance || 0;
      state.sensitivity_calcifications = rest.sensitivity_calcifications || 'medium';
      state.sensitivity_opacities = rest.sensitivity_opacities || 'medium';
      state.premium_features = rest.premium_features || initialState.premium_features;

      state.quality_system = rest.quality_system || initialState.quality_system;
      state.quailty_points_export = rest.quailty_points_export || initialState.quailty_points_export;
      state.dicom_server_aet = rest.dicom_server_aet || '';

      if (user_settings) {
        state.user_settings.presentation_lesions =
          user_settings.presentation_lesions || initialState.user_settings.presentation_lesions;
        state.user_settings.presentation_quality =
          user_settings.presentation_parenchyma || initialState.user_settings.presentation_quality;
        state.user_settings.inverted_view_position =
          user_settings.inverted_view_position || initialState.user_settings.inverted_view_position;
        state.user_settings.priors_report =
          user_settings.priors_report || initialState.user_settings.priors_report;
        state.user_settings.priors_number_limit =
          user_settings.priors_number_limit ?? initialState.user_settings.priors_number_limit;
        state.user_settings.priors_years_limit =
          user_settings.priors_years_limit ?? initialState.user_settings.priors_years_limit;
      }

      if (report_configuration) {
        state.report_configuration.language =
          report_configuration.language ?? initialState.report_configuration.language;
        state.report_configuration.file_format =
          report_configuration.file_format ?? initialState.report_configuration.file_format;
        state.report_configuration.sending_delay =
          report_configuration.sending_delay ?? initialState.report_configuration.sending_delay;
        state.report_configuration.contour_shown = !!report_configuration.contour_shown;
        state.report_configuration.projections_big = !!report_configuration.projections_big;
        state.report_configuration.hide_operator_name = !!report_configuration.hide_operator_name;
        state.report_configuration.image_compression = !!report_configuration.image_compression;
        state.report_configuration.show_missed_findings = !!report_configuration.show_missed_findings;
        state.report_configuration.crop_to_breast = !!report_configuration.crop_to_breast;
        let lesions = report_configuration.lesions_to_show;
        if (typeof lesions === 'string') {
          try {
            lesions = JSON.parse(lesions);
          } catch {
            lesions = {};
          }
        }
        state.report_configuration.lesions_to_show = {
          ...initialState.report_configuration.lesions_to_show,
          ...(lesions || {}),
        };
      }
    },

    setReportConfiguration: (state, action) => {
      const { lesions_to_show, ...rest } = action.payload;
      Object.assign(state.report_configuration, rest);
      if (lesions_to_show) {
        state.report_configuration.lesions_to_show = {
          ...state.report_configuration.lesions_to_show,
          ...lesions_to_show,
        };
      }
    },

    setLesionToShow: (state, action) => {
      const { key, value } = action.payload;
      state.report_configuration.lesions_to_show[key] = value;
    },

    setLanguage: (state, action) => {
      state.language = action.payload;
    },
  },
});

export const { setGlobalSettings, setLanguage, setReportConfiguration, setLesionToShow } =
  globalSettingsSlice.actions;

export const selectReportConfiguration = (state) => state.global_settings.report_configuration;
export const selectLesionsToShow = (state) => state.global_settings.report_configuration.lesions_to_show;
export const selectUserSettings = (state) => state.global_settings.user_settings;

export default globalSettingsSlice.reducer;

export const fetchGlobalSettings = () => {
  return async (dispatch) => {
    const delay = 5;
    let response;

    while (true) {
      response = await fetchGet('global_settings', 'bbox');
      if (response.success) break;
      console.log(`Fetching global settings failed. Retrying in ${delay} seconds`);
      await new Promise((r) => setTimeout(r, delay * 1000));
    }
    dispatch(setGlobalSettings(response.data));
  };
};
